import {
  Compensation,
  CompensationOptions,
  RefundMethod
} from '@/domain/refund'
import {
  CostItem,
  CostType,
  ItemAction,
  OpenExchangeItem,
  OrderedItem,
  ProcessStatus,
  ReturnDocument,
  ReturnedItem,
  ReturnProcess,
  ExchangeShippedNotice,
  CancelExchangeOutcome,
  CancelExchangePreview
} from '@/domain/returns'
import { PerformedReview, ReviewStatus } from '@/domain/review'
import {
  PbCompensation,
  PbExtraLineItem,
  PbExtraLineItemTypeEnum,
  PbGetCancelExchangePreviewResponse,
  PbGetCancelExchangePreviewResponseOutcomeEnum,
  PbGetReturnSummaryResponse,
  PbItemReview,
  PbItemReviewOutcomeEnum,
  PbOpenExchangeItem,
  PbOrderedItem,
  PbReturnedItem,
  PbReturnedItemActionEnum,
  PbReturnSummaryKeepYourItemTypeEnum,
  PbReturnSummaryRefundPaymentMethodEnum,
  PbReturnSummaryRefundTimingEnum,
  PbReturnSummaryStatusEnum,
  PbUploadDocumentsInfoUploadedDocuments
} from '@itsrever/rever-api'

export function toReturnProcess(
  returnSummary: PbGetReturnSummaryResponse
): ReturnProcess {
  const summary = returnSummary.return_summary
  const compensation = toCompensation(summary?.compensation)
  const process: ReturnProcess = {
    processId: summary?.basic_process_data?.process_id ?? '',
    platform: summary?.basic_process_data?.platform,
    orderPlatformUrl: summary?.navigation?.admin_html_order_url,
    ecommerceId: summary?.basic_process_data?.ecommerce_id ?? '',
    purchasedDate: new Date(
      summary?.basic_process_data?.order_purchased_at ?? ''
    ),
    returnDate: new Date(summary?.basic_process_data?.started_at ?? ''),
    customerPrintedOrderId:
      summary?.basic_process_data?.customer_printed_order_id ?? '',
    orderId: summary?.basic_process_data?.order_id ?? '',
    customer: {
      name: getName(
        summary?.customer?.first_name,
        summary?.customer?.last_name
      ),
      email: summary?.customer?.email ?? '-',
      phone: summary?.customer?.address?.phone ?? '-',
      address: {
        addressLine1: summary?.customer?.address?.address_line_1 ?? '',
        addressLine2: summary?.customer?.address?.address_line_2 ?? '',
        city: summary?.customer?.address?.city ?? '',
        zip: summary?.customer?.address?.postal_code ?? '',
        countryCode: summary?.customer?.address?.country_code ?? ''
      },
      preferredLanguage: summary?.customer?.preferred_language ?? ''
    },
    exchangeOrderNumber:
      summary?.compensation?.exchange?.exchange_order?.order_number ?? '',
    exchangeOrderUrl:
      summary?.compensation?.exchange?.exchange_order?.navigation
        ?.admin_html_order_url ?? '',
    exchangeOrderShippedNotice: toExchangeShippedNotice(
      summary?.compensation?.exchange?.exchange_order?.shipped_notice
    ),
    promoCode: summary?.compensation?.promocode?.promocode?.code ?? '',
    giftCardCode: summary?.compensation?.gift_card?.gift_card?.code ?? '',
    returnedItems: toReturnedItems(
      summary?.returned_items?.returned_items ?? []
    ),
    orderedItems: toOrderedItems(summary?.ordered_items?.ordered_items ?? []),
    openExchangeItems: toOpenExchangeItems(
      summary?.open_exchange_items?.open_exchange_items ?? []
    ),
    extraLineItems: toExtraItems(
      summary?.extra_line_items?.extra_line_items ?? []
    ),
    refundPaymentMethod: toRefundPaymentMethod(summary?.refund_payment_method),
    compensation,
    currencyCode: summary?.currency_code ?? 'EUR',
    isAmountEditable:
      summary?.compensation?.editable_refund_amount
        ?.is_refund_amount_editable ?? false,
    maximumRefundAmount: {
      amount:
        summary?.compensation?.editable_refund_amount?.maximum_refundable_amount
          ?.presentment_money?.amount ?? '',
      currency:
        summary?.compensation?.editable_refund_amount?.maximum_refundable_amount
          ?.presentment_money?.currency_code ?? ''
    },
    isKeepYourItem: isKeepYourItem(summary?.keep_your_item_type),
    returnDocuments: toReturnDocuments(
      summary?.upload_documents_info?.uploaded_documents ?? []
    ),
    uploadDocsEnabled:
      summary?.upload_documents_info?.upload_documents_enabled ?? false,
    authorizationPending:
      summary?.status === 'RETURN_PROCESS_STATUS_AUTHORIZATION_PENDING' ||
      summary?.status === 'RETURN_PROCESS_STATUS_DOCUMENT_UPLOAD_PENDING',
    unauthorized: summary?.status === 'RETURN_PROCESS_STATUS_NOT_AUTHORIZED',
    isCanceled: summary?.status === 'RETURN_PROCESS_STATUS_CANCELED',
    status: toStatus(summary?.status),
    isRefunded: isRefunded(compensation),
    hasIrreversibleRefund: hasIrreversibleRefund(compensation),
    hasExecutedStoreCreditRefund: hasExecutedStoreCreditRefund(compensation),
    refundTiming:
      summary?.refund_timing ?? PbReturnSummaryRefundTimingEnum.NoTiming,
    btFallbackTriggered:
      summary?.compensation?.original_payment_method?.bt_fallback_triggered ??
      false,
    firstRefundDate: getFirstRefundDate(summary?.compensation),
    isOnlyExchange: isOnlyExchange(summary?.compensation),
    commitedAmount: getCommitedAmount(summary?.compensation),
    isClaim: summary?.is_claim ?? false
  }
  return process
}

function toExchangeShippedNotice(
  notice?: string
): ExchangeShippedNotice | undefined {
  switch (notice) {
    case 'SHIPPED_NOTICE_DEDUCTED_FROM_REFUND':
      return 'deducted_from_refund'
    case 'SHIPPED_NOTICE_NOT_COVERED':
      return 'not_covered'
    default:
      return undefined
  }
}

function getName(firstName?: string, lastName?: string) {
  if (!firstName && !lastName) return '-'
  if (!firstName) return lastName ?? ''
  if (!lastName) return firstName ?? ''
  return `${firstName} ${lastName}`
}

function toReturnedItems(returnedItems: PbReturnedItem[]): ReturnedItem[] {
  return returnedItems.map((item) => {
    return {
      product: {
        imageUrl: item.product_info?.image_url ?? '',
        productId: item.product_info?.product_id ?? '',
        productName: item.product_info?.product_name ?? '',
        productReference: item.product_info?.product_reference ?? '',
        sku: item.product_info?.sku ?? '',
        variantEan: item.product_info?.variant_ean ?? '',
        variantId: item.product_info?.variant_id ?? '',
        variantName: item.product_info?.variant_name ?? '',
        variantReference: item.product_info?.variant_reference ?? ''
      },
      action: toRefundActions(item.action),
      platformlineItemId: item.platform_line_item_id ?? '',
      reverLineItemId: item.rever_id ?? '',
      quantity: item.quantity ?? 1,
      returnReasonKey: item.return_reason_key ?? '',
      returnReasonComment: item.return_reason_comment ?? '',
      unitPrice: {
        amount: item.unit_price?.presentment_money?.amount ?? '',
        currency: item.unit_price?.presentment_money?.currency_code ?? ''
      },
      total: {
        amount: item.total?.presentment_money?.amount ?? '',
        currency: item.total?.presentment_money?.currency_code ?? ''
      }
    }
  })
}

function toRefundActions(action?: PbReturnedItemActionEnum): ItemAction {
  switch (action) {
    case PbReturnedItemActionEnum.Exchange:
      return 'EXCHANGE'
    case PbReturnedItemActionEnum.Refund:
      return 'RETURN'
    default:
      return 'RETURN'
  }
}

function toOrderedItems(orderedItems: PbOrderedItem[]): OrderedItem[] {
  return orderedItems.map((item) => {
    return {
      productName: item.product_name ?? '',
      variantName: item.variant_name ?? '',
      quantity: item.quantity ?? 1,
      total: {
        amount: item.total?.presentment_money?.amount ?? '',
        currency: item.total?.presentment_money?.currency_code ?? ''
      }
    }
  })
}

function toOpenExchangeItems(
  openExchangeItems: PbOpenExchangeItem[]
): OpenExchangeItem[] {
  return openExchangeItems.map((item) => {
    return {
      productName: item.product_name ?? '',
      quantity: item.quantity ?? 1,
      total: {
        amount: item.total?.presentment_money?.amount ?? '',
        currency: item.total?.presentment_money?.currency_code ?? ''
      }
    }
  })
}

function toExtraItems(extraLineItems: PbExtraLineItem[]): CostItem[] {
  return extraLineItems.map((item) => {
    return {
      total: {
        amount: item.total?.presentment_money?.amount ?? '',
        currency: item.total?.presentment_money?.currency_code ?? ''
      },
      type: toCostType(item.type)
    }
  })
}

function toCostType(type?: PbExtraLineItemTypeEnum): CostType {
  switch (type) {
    case PbExtraLineItemTypeEnum.ReturnShippingCost:
      return 'SHIPPING_COST'
    case PbExtraLineItemTypeEnum.OriginalShippingRefund:
      return 'ORIGINAL_SHIPPING_REFUND'
    case PbExtraLineItemTypeEnum.KeepYourItem:
      return 'KEEP_YOUR_ITEM'
    case PbExtraLineItemTypeEnum.RdvSurplus:
      return 'RDV_SURPLUS'
    case PbExtraLineItemTypeEnum.ServiceFee:
      return 'SERVICE_FEE'
    case PbExtraLineItemTypeEnum.OriginalPromotionBroken:
      return 'ORIGINAL_PROMOTION_BROKEN'
    case PbExtraLineItemTypeEnum.ExchangePromotion:
      return 'EXCHANGE_PROMOTION'
    case PbExtraLineItemTypeEnum.InstantExchange:
      return 'INSTANT_EXCHANGE'
    case PbExtraLineItemTypeEnum.FreeReturnPromotion:
      return 'FREE_RETURN_PROMOTION'
    case PbExtraLineItemTypeEnum.ReturnShippingTax:
      return 'RETURN_SHIPPING_TAX'
    case PbExtraLineItemTypeEnum.ExternalRefund:
      return 'EXTERNAL_REFUND'
    case PbExtraLineItemTypeEnum.PlatformRefundableCap:
      return 'PLATFORM_REFUNDABLE_CAP'
    default:
      return 'SERVICE_FEE'
  }
}

function toRefundPaymentMethod(
  method?: PbReturnSummaryRefundPaymentMethodEnum
): RefundMethod {
  switch (method) {
    case PbReturnSummaryRefundPaymentMethodEnum.BankTransfer:
      return 'BANK_TRANSFER'
    case PbReturnSummaryRefundPaymentMethodEnum.Original:
      return 'ORIGINAL_PM'
    case PbReturnSummaryRefundPaymentMethodEnum.PromoCode:
      return 'PROMO_CODE'
    case PbReturnSummaryRefundPaymentMethodEnum.GiftCard:
      return 'GIFT_CARD'
    default:
      return 'NO_REFUND_METHOD'
  }
}

function toCompensation(compensation?: PbCompensation): Compensation {
  return {
    bankTransfer: {
      initialCommitment: {
        amount:
          compensation?.bank_transfer?.summary?.initial_commitment
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.bank_transfer?.summary?.initial_commitment
            ?.presentment_money?.currency_code ?? ''
      },
      adjustments_list: compensation?.bank_transfer?.summary?.adjustments_list,
      adjustments: {
        amount:
          compensation?.bank_transfer?.summary?.adjustments?.presentment_money
            ?.amount ?? '',
        currency:
          compensation?.bank_transfer?.summary?.adjustments?.presentment_money
            ?.currency_code ?? ''
      },
      adjustedCommitment: {
        amount:
          compensation?.bank_transfer?.summary?.adjusted_commitment
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.bank_transfer?.summary?.adjusted_commitment
            ?.presentment_money?.currency_code ?? ''
      },
      executed: {
        amount:
          compensation?.bank_transfer?.summary?.executed?.presentment_money
            ?.amount ?? '',
        currency:
          compensation?.bank_transfer?.summary?.executed?.presentment_money
            ?.currency_code ?? ''
      },
      adjustmentReason: compensation?.bank_transfer?.summary?.adjustment_reason
    },
    originalPM: {
      initialCommitment: {
        amount:
          compensation?.original_payment_method?.summary?.initial_commitment
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.original_payment_method?.summary?.initial_commitment
            ?.presentment_money?.currency_code ?? ''
      },
      adjustments_list:
        compensation?.original_payment_method?.summary?.adjustments_list,
      adjustments: {
        amount:
          compensation?.original_payment_method?.summary?.adjustments
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.original_payment_method?.summary?.adjustments
            ?.presentment_money?.currency_code ?? ''
      },
      adjustedCommitment: {
        amount:
          compensation?.original_payment_method?.summary?.adjusted_commitment
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.original_payment_method?.summary?.adjusted_commitment
            ?.presentment_money?.currency_code ?? ''
      },
      executed: {
        amount:
          compensation?.original_payment_method?.summary?.executed
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.original_payment_method?.summary?.executed
            ?.presentment_money?.currency_code ?? ''
      },
      adjustmentReason:
        compensation?.original_payment_method?.summary?.adjustment_reason
    },
    promoCode: {
      initialCommitment: {
        amount:
          compensation?.promocode?.summary?.initial_commitment
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.promocode?.summary?.initial_commitment
            ?.presentment_money?.currency_code ?? ''
      },
      adjustments_list: compensation?.promocode?.summary?.adjustments_list,
      adjustments: {
        amount:
          compensation?.promocode?.summary?.adjustments?.presentment_money
            ?.amount ?? '',
        currency:
          compensation?.promocode?.summary?.adjustments?.presentment_money
            ?.currency_code ?? ''
      },
      adjustedCommitment: {
        amount:
          compensation?.promocode?.summary?.adjusted_commitment
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.promocode?.summary?.adjusted_commitment
            ?.presentment_money?.currency_code ?? ''
      },
      executed: {
        amount:
          compensation?.promocode?.summary?.executed?.presentment_money
            ?.amount ?? '',
        currency:
          compensation?.promocode?.summary?.executed?.presentment_money
            ?.currency_code ?? ''
      },
      adjustmentReason: compensation?.promocode?.summary?.adjustment_reason
    },
    giftCard: {
      initialCommitment: {
        amount:
          compensation?.gift_card?.summary?.initial_commitment
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.gift_card?.summary?.initial_commitment
            ?.presentment_money?.currency_code ?? ''
      },
      adjustments_list: compensation?.gift_card?.summary?.adjustments_list,
      adjustments: {
        amount:
          compensation?.gift_card?.summary?.adjustments?.presentment_money
            ?.amount ?? '',
        currency:
          compensation?.gift_card?.summary?.adjustments?.presentment_money
            ?.currency_code ?? ''
      },
      adjustedCommitment: {
        amount:
          compensation?.gift_card?.summary?.adjusted_commitment
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.gift_card?.summary?.adjusted_commitment
            ?.presentment_money?.currency_code ?? ''
      },
      executed: {
        amount:
          compensation?.gift_card?.summary?.executed?.presentment_money
            ?.amount ?? '',
        currency:
          compensation?.gift_card?.summary?.executed?.presentment_money
            ?.currency_code ?? ''
      },
      adjustmentReason: compensation?.gift_card?.summary?.adjustment_reason
    },
    exchange: {
      initialCommitment: {
        amount:
          compensation?.exchange?.summary?.initial_commitment?.presentment_money
            ?.amount ?? '',
        currency:
          compensation?.exchange?.summary?.initial_commitment?.presentment_money
            ?.currency_code ?? ''
      },
      adjustments: {
        amount:
          compensation?.exchange?.summary?.adjustments?.presentment_money
            ?.amount ?? '',
        currency:
          compensation?.exchange?.summary?.adjustments?.presentment_money
            ?.currency_code ?? ''
      },
      adjustments_list: compensation?.exchange?.summary?.adjustments_list,
      adjustedCommitment: {
        amount:
          compensation?.exchange?.summary?.adjusted_commitment
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.exchange?.summary?.adjusted_commitment
            ?.presentment_money?.currency_code ?? ''
      },
      executed: {
        amount:
          compensation?.exchange?.summary?.executed?.presentment_money
            ?.amount ?? '',
        currency:
          compensation?.exchange?.summary?.executed?.presentment_money
            ?.currency_code ?? ''
      },
      adjustmentReason: compensation?.exchange?.summary?.adjustment_reason
    },
    statusSummary: {
      externalRefundDetails:
        compensation?.compensation_status_summary?.external_refund_details,
      pendingRefundAmount: {
        amount:
          compensation?.compensation_status_summary?.total_pending_refund_amount
            ?.presentment_money?.amount ?? '',
        currency:
          compensation?.compensation_status_summary?.total_pending_refund_amount
            ?.presentment_money?.currency_code ?? ''
      }
    }
  }
}

function isKeepYourItem(type?: PbReturnSummaryKeepYourItemTypeEnum): boolean {
  if (!type) return false
  return (
    type !== PbReturnSummaryKeepYourItemTypeEnum.None &&
    type !== PbReturnSummaryKeepYourItemTypeEnum.Unspecified
  )
}

export function toReturnDocuments(
  docs: PbUploadDocumentsInfoUploadedDocuments[]
): ReturnDocument[] {
  if (!docs) return []
  return docs.map((doc) => ({
    information: doc.document_info ?? '',
    url: doc.document_url ?? '',
    lineItemId: doc.line_item_id || undefined,
    returnReasonKey: doc.return_reason_key || undefined
  }))
}

function toStatus(status?: PbReturnSummaryStatusEnum): ProcessStatus {
  switch (status) {
    case PbReturnSummaryStatusEnum.AuthorizationPending:
      return 'PENDING_AUTHORIZATION'
    case PbReturnSummaryStatusEnum.Canceled:
      return 'CANCELLED'
    case PbReturnSummaryStatusEnum.Collected:
      return 'COLLECTED'
    case PbReturnSummaryStatusEnum.Completed:
      return 'COMPLETED'
    case PbReturnSummaryStatusEnum.DocumentUploadPending:
      return 'DOCUMENT_UPLOAD_PENDING'
    case PbReturnSummaryStatusEnum.NotAuthorized:
      return 'NOT_AUTHORIZED'
    case PbReturnSummaryStatusEnum.ReviewRequired:
      return 'REVIEW_REQUIRED'
    case PbReturnSummaryStatusEnum.Started:
      return 'STARTED'
    default:
      return 'STARTED'
  }
}

// NaN guard: a missing executed amount must count as 0, never poison the sum.
const executedAmount = (option?: CompensationOptions): number => {
  const amount = parseFloat(option?.executed?.amount ?? '')
  return Number.isNaN(amount) ? 0 : amount
}

const hasIrreversibleRefund = (compensationDetails: Compensation): boolean => {
  return (
    executedAmount(compensationDetails?.bankTransfer) +
      executedAmount(compensationDetails?.originalPM) >
    0
  )
}

const hasExecutedStoreCreditRefund = (
  compensationDetails: Compensation
): boolean => {
  return (
    executedAmount(compensationDetails?.giftCard) +
      executedAmount(compensationDetails?.promoCode) >
    0
  )
}

function isRefunded(compensationDetails: Compensation): boolean {
  return (
    executedAmount(compensationDetails?.bankTransfer) +
      executedAmount(compensationDetails?.exchange) +
      executedAmount(compensationDetails?.giftCard) +
      executedAmount(compensationDetails?.originalPM) +
      executedAmount(compensationDetails?.promoCode) >
    0
  )
}

function getFirstRefundDate(
  compensationDetails: PbCompensation | undefined
): Date | undefined {
  if (!compensationDetails) return undefined
  const refundDates: (Date | undefined)[] = []

  if (compensationDetails.bank_transfer?.refunds) {
    const btRefunds = compensationDetails.bank_transfer.refunds.map(
      (refund) => {
        if (!refund.created_at || refund.created_at === '') return undefined
        return new Date(refund.created_at)
      }
    )
    refundDates.push(...btRefunds)
  }
  if (compensationDetails.original_payment_method?.refunds) {
    const opmRefunds = compensationDetails.original_payment_method.refunds.map(
      (refund) => {
        if (!refund.created_at) return undefined
        return new Date(refund.created_at)
      }
    )
    refundDates.push(...opmRefunds)
  }
  if (compensationDetails.promocode?.promocode?.created_at) {
    refundDates.push(
      new Date(compensationDetails.promocode.promocode.created_at)
    )
  }
  if (compensationDetails.gift_card?.gift_card?.created_at) {
    refundDates.push(
      new Date(compensationDetails.gift_card.gift_card.created_at)
    )
  }
  if (refundDates.length === 0) return undefined
  refundDates.sort((a, b) => (a && b ? a.getTime() - b.getTime() : 0))
  return refundDates[0]
}

function isOnlyExchange(compensation: PbCompensation | undefined): boolean {
  if (!compensation) return false

  const exchangeAmount = parseFloat(
    compensation?.exchange?.summary?.adjusted_commitment?.presentment_money
      ?.amount ?? '0'
  )
  const bankTransferAmount = parseFloat(
    compensation?.bank_transfer?.summary?.adjusted_commitment?.presentment_money
      ?.amount ?? '0'
  )
  const giftCardAmount = parseFloat(
    compensation?.gift_card?.summary?.adjusted_commitment?.presentment_money
      ?.amount ?? '0'
  )
  const originalPMAmount = parseFloat(
    compensation?.original_payment_method?.summary?.adjusted_commitment
      ?.presentment_money?.amount ?? '0'
  )
  const promoCodeAmount = parseFloat(
    compensation?.promocode?.summary?.adjusted_commitment?.presentment_money
      ?.amount ?? '0'
  )

  return (
    exchangeAmount > 0 &&
    bankTransferAmount === 0 &&
    giftCardAmount === 0 &&
    originalPMAmount === 0 &&
    promoCodeAmount === 0
  )
}

function getCommitedAmount(compensation: PbCompensation | undefined): number {
  const bankTransferAmount = parseFloat(
    compensation?.bank_transfer?.summary?.adjusted_commitment?.presentment_money
      ?.amount ?? '0'
  )
  const giftCardAmount = parseFloat(
    compensation?.gift_card?.summary?.adjusted_commitment?.presentment_money
      ?.amount ?? '0'
  )
  const originalPMAmount = parseFloat(
    compensation?.original_payment_method?.summary?.adjusted_commitment
      ?.presentment_money?.amount ?? '0'
  )
  const promoCodeAmount = parseFloat(
    compensation?.promocode?.summary?.adjusted_commitment?.presentment_money
      ?.amount ?? '0'
  )

  return (
    bankTransferAmount + giftCardAmount + originalPMAmount + promoCodeAmount
  )
}

export function toPerformedReviews(reviews: PbItemReview[]): PerformedReview[] {
  if (!reviews) return []
  return reviews.map((rev) => {
    return {
      status: toReviewStatus(rev.outcome),
      reverLineItemId: rev.returned_item_rever_id ?? '',
      rejectReason: rev.reject_reason
    }
  })
}

function toReviewStatus(
  outcome: PbItemReviewOutcomeEnum | undefined
): ReviewStatus {
  switch (outcome) {
    case PbItemReviewOutcomeEnum.Approved:
      return 'APPROVED'
    case PbItemReviewOutcomeEnum.Declined:
      return 'DECLINED'
    case PbItemReviewOutcomeEnum.Missing:
      return 'MISSING'
    default:
      return 'APPROVED'
  }
}

const cancelExchangeOutcomes: Partial<
  Record<PbGetCancelExchangePreviewResponseOutcomeEnum, CancelExchangeOutcome>
> = {
  [PbGetCancelExchangePreviewResponseOutcomeEnum.NoExchange]: 'no_exchange',
  [PbGetCancelExchangePreviewResponseOutcomeEnum.NotHeld]: 'not_held',
  [PbGetCancelExchangePreviewResponseOutcomeEnum.NotSupported]: 'not_supported',
  [PbGetCancelExchangePreviewResponseOutcomeEnum.WillCancel]: 'will_cancel',
  [PbGetCancelExchangePreviewResponseOutcomeEnum.AlreadyShipped]:
    'already_shipped',
  [PbGetCancelExchangePreviewResponseOutcomeEnum.AlreadyCanceled]:
    'already_canceled'
}

// An outcome this build does not know falls back to the manual-cancel warning, never to silence.
export function toCancelExchangePreview(
  dto: PbGetCancelExchangePreviewResponse
): CancelExchangePreview {
  return {
    outcome:
      (dto.outcome && cancelExchangeOutcomes[dto.outcome]) ?? 'not_supported',
    exchangeOrderNumber: dto.exchange_order_number ?? ''
  }
}
